import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AppLayout from '../components/AppLayout';
import GroupChip from '../components/GroupChip';
import Avatar from '../components/Avatar';
import PetBadge from '../components/PetBadge';
import WishFormModal, { BLANK_WISH, wishToForm } from '../components/WishFormModal';
import { formatPrice, priorityBadge, safeHttpUrl } from '../utils/wishDisplay';
import { inbox as fetchInbox, acceptSuggestion, declineSuggestion, markSeen } from '../services/suggestions';

/**
 * My Wishes — the user's global wish list across all groups, mirroring the iOS
 * "My Wishes" tab (MyWishListView). These are always the viewer's OWN wishes,
 * so no claim/purchase state is ever shown here (owner-blindness: the API
 * masks it and we don't render it).
 */

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

export default function MyWishes() {
    const navigate = useNavigate();
    const [wishes, setWishes] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [flash, setFlash] = useState(null); // { type, text }
    const [modal, setModal] = useState(null); // { editingId, initialForm }
    const [suggestions, setSuggestions] = useState([]); // pending "ideas suggested for you"
    const [busySuggestionId, setBusySuggestionId] = useState(null);

    const notify = useCallback((type, text) => {
        setFlash({ type, text });
        if (type === 'success') setTimeout(() => setFlash(null), 3000);
    }, []);

    const reloadWishes = useCallback(async () => {
        const res = await api.get('/wishes');
        setWishes(res.data);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [wishesRes, groupsRes, catsRes, inboxRes] = await Promise.all([
                    api.get('/wishes'),
                    api.get('/groups'),
                    api.get('/wish-categories'),
                    fetchInbox().catch(() => []), // inbox is a nicety; never block the page
                ]);
                if (cancelled) return;
                setWishes(wishesRes.data);
                setMyGroups(groupsRes.data);
                setCategories(catsRes.data);
                setSuggestions(inboxRes || []);
                // Mark the visible pending suggestions as seen so the nav badge clears.
                for (const s of inboxRes || []) {
                    markSeen(s.id).catch(() => {});
                }
            } catch (err) {
                if (!cancelled) notify('error', 'Failed to load your wishes.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [notify]);

    const handleAcceptSuggestion = async (suggestion) => {
        setBusySuggestionId(suggestion.id);
        try {
            await acceptSuggestion(suggestion.id);
            setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
            // Accepting on a child's behalf mints the wish onto THEIR list, not yours —
            // saying "added to your list" would be simply untrue for a carer.
            notify('success', suggestion.recipient_child
                ? `Added to ${suggestion.recipient_child.name}'s list.`
                : 'Added to your list.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not add that idea.'));
        } finally {
            setBusySuggestionId(null);
        }
    };

    const handleDeclineSuggestion = async (suggestion) => {
        if (!window.confirm(`Decline "${suggestion.title}"? The sender will see it was declined.`)) return;
        setBusySuggestionId(suggestion.id);
        try {
            await declineSuggestion(suggestion.id);
            setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
            notify('success', 'Declined.');
        } catch (err) {
            notify('error', errText(err, 'Could not decline that idea.'));
        } finally {
            setBusySuggestionId(null);
        }
    };

    // Split the list into the user's own wishes and one section per pet they care
    // for (pet wishes come back here because they're user_id = the caretaker).
    const { ownWishes, petSections } = useMemo(() => {
        const own = [];
        const byPet = new Map();
        for (const w of wishes) {
            if (w.pet) {
                if (!byPet.has(w.pet.id)) byPet.set(w.pet.id, { pet: w.pet, wishes: [] });
                byPet.get(w.pet.id).wishes.push(w);
            } else {
                own.push(w);
            }
        }
        const sections = Array.from(byPet.values()).sort((a, b) =>
            (a.pet.name || '').localeCompare(b.pet.name || '')
        );
        return { ownWishes: own, petSections: sections };
    }, [wishes]);

    const openCreate = () => setModal({ editingId: null, initialForm: { ...BLANK_WISH } });

    const openEdit = async (wish) => {
        try {
            // Fetch full detail so we know which groups the wish is shared into.
            const res = await api.get(`/wishes/${wish.id}`);
            setModal({ editingId: wish.id, initialForm: wishToForm(res.data) });
        } catch (err) {
            notify('error', errText(err, 'Could not open that wish for editing.'));
        }
    };

    const handleDelete = async (wish) => {
        if (!window.confirm(`Delete "${wish.title}"? This can't be undone.`)) return;
        try {
            await api.delete(`/wishes/${wish.id}`);
            notify('success', 'Wish deleted.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not delete the wish.'));
        }
    };

    const handleArchive = async (wish) => {
        try {
            await api.post(`/wishes/${wish.id}/archive`);
            notify('success', 'Wish archived.');
            reloadWishes();
        } catch (err) {
            notify('error', errText(err, 'Could not archive the wish.'));
        }
    };

    return (
        <AppLayout>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">My Wishes</h2>
                <button
                    onClick={openCreate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                    + Add Wish
                </button>
            </div>

            {flash && (
                <div
                    className={`mb-4 p-3 rounded-md text-sm border ${
                        flash.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-700'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                >
                    {flash.text}
                </div>
            )}

            {/* Ideas suggested for you — pinned inbox (cloned from the Dashboard
                "Pending Invitations" block). Accepting mints a wish that then
                appears in the grid below. */}
            {suggestions.length > 0 && (
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Recommended for you</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {suggestions.map((s) => (
                            <SuggestionCard
                                key={s.id}
                                suggestion={s}
                                busy={busySuggestionId === s.id}
                                onAccept={() => handleAcceptSuggestion(s)}
                                onDecline={() => handleDeclineSuggestion(s)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-12 text-gray-500">Loading…</div>
            ) : wishes.length === 0 && suggestions.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    <p className="mb-3">Your wish list is empty.</p>
                    <button onClick={openCreate} className="text-blue-600 hover:text-blue-700 font-medium">
                        Add your first wish
                    </button>
                </div>
            ) : (
                <div className="space-y-8">
                    {ownWishes.length > 0 && (
                        <div>
                            {petSections.length > 0 && (
                                <h3 className="text-md font-semibold text-gray-900 mb-3">Your own wishes</h3>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {ownWishes.map((wish) => (
                                    <OwnWishCard
                                        key={wish.id}
                                        wish={wish}
                                        onEdit={() => openEdit(wish)}
                                        onArchive={() => handleArchive(wish)}
                                        onDelete={() => handleDelete(wish)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {petSections.map((section) => (
                        <div key={section.pet.id}>
                            <div className="flex items-center gap-3 mb-3">
                                <button
                                    onClick={() => navigate(`/pet/${section.pet.id}`)}
                                    title="View pet profile"
                                    className="focus:outline-none"
                                >
                                    <Avatar name={section.pet.name} url={section.pet.avatar_url} color={section.pet.avatar_color} seed={section.pet.id} size={32} />
                                </button>
                                <h3 className="text-md font-semibold text-gray-900">{section.pet.name}'s wishes</h3>
                                <PetBadge species={section.pet.species} />
                                <span className="text-xs text-gray-400">
                                    {section.wishes.length} item{section.wishes.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {section.wishes.map((wish) => (
                                    <OwnWishCard
                                        key={wish.id}
                                        wish={wish}
                                        onEdit={() => openEdit(wish)}
                                        onArchive={() => handleArchive(wish)}
                                        onDelete={() => handleDelete(wish)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {modal && (
                <WishFormModal
                    editingId={modal.editingId}
                    initialForm={modal.initialForm}
                    myGroups={myGroups}
                    categories={categories}
                    onClose={() => setModal(null)}
                    onSaved={() => {
                        setModal(null);
                        reloadWishes();
                    }}
                    notify={notify}
                />
            )}
        </AppLayout>
    );
}

/** A wish card in OWNER view only — never any claim/purchase state. */
function OwnWishCard({ wish, onEdit, onArchive, onDelete }) {
    const price = formatPrice(wish.price_min, wish.price_max);

    return (
        <div className="bg-white rounded-lg shadow p-4 flex flex-col">
            {wish.image_url && (
                <img src={wish.image_url} alt="" className="w-full h-32 object-cover rounded-md mb-3" />
            )}
            <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900">{wish.title}</h3>
                {wish.priority && (
                    <span className={`text-xs px-2 py-0.5 rounded ${priorityBadge(wish.priority)}`}>
                        {wish.priority}
                    </span>
                )}
            </div>

            {wish.description && <p className="text-sm text-gray-600 mt-2">{wish.description}</p>}

            <div className="mt-2 space-y-1 text-sm">
                {price && <p className="text-gray-700">{price}</p>}
                {wish.quantity_desired > 1 && <p className="text-gray-500 text-xs">Wants {wish.quantity_desired}</p>}
                {wish.category && <p className="text-gray-500 text-xs">{wish.category.icon} {wish.category.name}</p>}
                <div className="flex flex-wrap gap-1">
                    {wish.multiple_ok && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">Multiple OK</span>}
                    {wish.is_generic && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Idea</span>}
                    {wish.event && <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">🎉 {wish.event.name}</span>}
                </div>
                {(wish.groups || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                        {wish.groups.map((g) => (
                            <GroupChip key={g.id} name={g.name} icon={g.icon} color={g.color} seed={g.id} />
                        ))}
                    </div>
                )}
                {safeHttpUrl(wish.url) && (
                    <a href={safeHttpUrl(wish.url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs inline-block">
                        View product ↗
                    </a>
                )}
            </div>

            <div className="mt-auto pt-3 flex gap-2">
                <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-600 mr-auto">Your wish</span>
                <button onClick={onEdit} className="text-sm text-gray-600 hover:text-gray-900">Edit</button>
                <button onClick={onArchive} className="text-sm text-gray-600 hover:text-gray-900">Archive</button>
                <button onClick={onDelete} className="text-sm text-red-600 hover:text-red-700">Delete</button>
            </div>
        </div>
    );
}

/**
 * A pending "idea suggested for you" card. Cloned from the Dashboard "Pending
 * Invitations" gradient card: shows the idea (title/image/price), who sent it,
 * their message, and Add to my list / No thanks actions.
 */
function SuggestionCard({ suggestion, busy, onAccept, onDecline }) {
    const price = formatPrice(suggestion.price_min, suggestion.price_max);
    const sender = suggestion.sender;

    return (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-5 shadow-sm flex flex-col">
            <div className="flex items-center gap-2 mb-3">
                <Avatar name={sender?.name} url={sender?.avatar_url} color={sender?.avatar_color} seed={sender?.id} size={32} />
                <p className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">{sender?.name || 'Someone'}</span> recommended this
                    {/* A recommendation FOR A CHILD lands in every carer's inbox, so it
                        has to say who it is for — otherwise a carer sees an idea that
                        isn't obviously about them and has no way to tell. */}
                    {suggestion.recipient_child && (
                        <> for <span className="font-medium text-gray-900">{suggestion.recipient_child.name}</span></>
                    )}
                </p>
            </div>

            {suggestion.image_url && (
                <img src={suggestion.image_url} alt="" className="w-full h-32 object-cover rounded-md mb-3" />
            )}

            <h4 className="font-semibold text-gray-900">{suggestion.title}</h4>
            {price && <p className="text-sm text-gray-700 mt-1">{price}</p>}
            {suggestion.category && (
                <p className="text-gray-500 text-xs mt-1">{suggestion.category.icon} {suggestion.category.name}</p>
            )}

            {suggestion.message && (
                <p className="text-sm text-gray-700 italic mt-2">“{suggestion.message}”</p>
            )}

            {safeHttpUrl(suggestion.url) && (
                <a href={safeHttpUrl(suggestion.url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs inline-block mt-2">
                    View product ↗
                </a>
            )}

            <div className="mt-auto pt-4 flex gap-2">
                <button
                    onClick={onAccept}
                    disabled={busy}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {busy ? 'Adding…' : 'Add to my list'}
                </button>
                <button
                    onClick={onDecline}
                    disabled={busy}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm font-medium disabled:opacity-50"
                >
                    No thanks
                </button>
            </div>
        </div>
    );
}
